import { diffWords } from 'diff';
import React from 'react';

export const normalise = (s: string) =>
  s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}]/gu, ' ')
    .toLowerCase();

const CJK_CHAR = /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]$/u;

const isCjkCharacter = (char: string) => CJK_CHAR.test(char);

const isAlphanumeric = (char: string) => /^[\p{L}\p{N}]$/u.test(char);

export const normalizedTextSeparator = (left: string, right: string) =>
  left && right && !(isCjkCharacter(left.at(-1) ?? '') || isCjkCharacter(right[0])) ? ' ' : '';

const CJK_SOURCE = '\\p{Script=Han}\\p{Script=Hiragana}\\p{Script=Katakana}\\p{Script=Hangul}';
const SPACE_AFTER_CJK = new RegExp(`([${CJK_SOURCE}])\\s+(?=[\\p{L}\\p{N}])`, 'gu');
const SPACE_BEFORE_CJK = new RegExp(`([\\p{L}\\p{N}])\\s+(?=[${CJK_SOURCE}])`, 'gu');

export const normaliseForMatch = (text: string) =>
  normalise(text)
    .replace(/\s+/g, ' ')
    .trim()
    .replace(SPACE_AFTER_CJK, '$1')
    .replace(SPACE_BEFORE_CJK, '$1');

export type SourceOffset = { start: number; end: number };

/** Normalize PDF text while retaining UTF-16 offsets into the original text node. */
export function normaliseTextWithOffsets(raw: string): {
  text: string;
  sourceOffsets: SourceOffset[];
} {
  let text = '';
  const sourceOffsets: SourceOffset[] = [];
  let rawOffset = 0;
  let pendingSeparator: SourceOffset | null = null;

  const emitPendingSeparator = () => {
    if (!pendingSeparator || !text) return;
    text += ' ';
    sourceOffsets.push(pendingSeparator);
  };

  const recordSeparator = (start: number, end: number) => {
    if (!text) return;
    pendingSeparator = pendingSeparator ? { start: pendingSeparator.start, end } : { start, end };
  };

  for (const rawChar of raw) {
    const rawEnd = rawOffset + rawChar.length;
    const normalized = rawChar.normalize('NFKC').toLowerCase();
    for (const normalizedChar of normalized) {
      if (!isAlphanumeric(normalizedChar)) {
        recordSeparator(rawOffset, rawEnd);
        continue;
      }
      const previousChar = text.at(-1) ?? '';
      if (!isCjkCharacter(previousChar) && !isCjkCharacter(normalizedChar)) {
        emitPendingSeparator();
      }
      text += normalizedChar;
      const offsetStart = rawOffset;
      const offsetEnd = rawEnd;
      sourceOffsets.push(
        ...Array.from({ length: normalizedChar.length }, () => ({
          start: offsetStart,
          end: offsetEnd,
        })),
      );
      pendingSeparator = null;
    }
    rawOffset = rawEnd;
  }

  return { text, sourceOffsets };
}

export type OffsetDivMapEntry = {
  div: HTMLElement;
  start: number;
  end: number;
  sourceOffsets: SourceOffset[];
};

export type TextMatchFragment = {
  div: HTMLElement;
  startOffset: number;
  endOffset: number;
};

function getTextDivs(textLayerDiv: HTMLElement): HTMLElement[] {
  const positionedSpans = Array.from(
    textLayerDiv.querySelectorAll<HTMLElement>('span[role="presentation"]'),
  );
  if (positionedSpans.length > 0) return positionedSpans;
  return Array.from(textLayerDiv.children) as HTMLElement[];
}

export function buildTextIndexWithOffsets(
  textLayerDiv: HTMLElement,
  boilerplate?: Set<string>,
): { fullText: string; divMap: OffsetDivMapEntry[] } {
  const textDivs = getTextDivs(textLayerDiv);
  let fullText = '';
  const divMap: OffsetDivMapEntry[] = [];

  for (const div of textDivs) {
    const { text, sourceOffsets } = normaliseTextWithOffsets(div.textContent ?? '');
    if (!text || boilerplate?.has(text)) continue;
    fullText += normalizedTextSeparator(fullText, text);
    divMap.push({
      div,
      start: fullText.length,
      end: fullText.length + text.length,
      sourceOffsets,
    });
    fullText += text;
  }

  return { fullText, divMap };
}

export function getMatchFragments(
  divMap: OffsetDivMapEntry[],
  matchIdx: number,
  matchEnd: number,
): TextMatchFragment[] {
  const fragments: TextMatchFragment[] = [];
  for (const entry of divMap) {
    const overlapStart = Math.max(matchIdx, entry.start);
    const overlapEnd = Math.min(matchEnd, entry.end);
    if (overlapStart >= overlapEnd) continue;

    const localStart = overlapStart - entry.start;
    const localEnd = overlapEnd - entry.start;
    const firstOffset = entry.sourceOffsets[localStart];
    const lastOffset = entry.sourceOffsets[localEnd - 1];
    if (!firstOffset || !lastOffset) continue;
    fragments.push({
      div: entry.div,
      startOffset: firstOffset.start,
      endOffset: lastOffset.end,
    });
  }
  return fragments;
}

export function scoreDiffChunk(
  chunk: string,
  needle: string,
  anchor: number,
): { ratio: number; matchEnd: number } {
  const changes = diffWords(chunk, needle);
  let common = 0;
  let total = 0;
  for (const c of changes) {
    const len = (c.value ?? '').length;
    total += len;
    if (!c.added && !c.removed) common += len;
  }
  const ratio = common / Math.max(total, 1);
  let needleConsumed = 0;
  let fullTextConsumed = 0;
  for (const c of changes) {
    if (needleConsumed >= needle.length) break;
    const cLen = (c.value ?? '').length;
    if (c.added) {
      needleConsumed += cLen;
    } else if (c.removed) {
      fullTextConsumed += cLen;
    } else {
      needleConsumed += cLen;
      fullTextConsumed += cLen;
    }
  }
  return { ratio, matchEnd: anchor + fullTextConsumed };
}

export function fuzzyFindInFullText(
  fullText: string,
  needle: string,
): { matchIdx: number; matchEnd: number } | null {
  let firstWord = needle.trim().split(/\s+/)[0];
  if (!firstWord) return null;
  if (firstWord.length < 3) firstWord += ' ';

  const windowSize = Math.floor(needle.length * 1.5);
  let bestRatio = 0;
  let bestSpan: { matchIdx: number; matchEnd: number } | null = null;
  let searchFrom = 0;

  while (searchFrom < fullText.length) {
    const anchor = fullText.indexOf(firstWord, searchFrom);
    if (anchor === -1) break;
    const { ratio, matchEnd } = scoreDiffChunk(
      fullText.slice(anchor, anchor + windowSize),
      needle,
      anchor,
    );
    if (ratio > bestRatio) {
      bestRatio = ratio;
      bestSpan = { matchIdx: anchor, matchEnd };
      if (bestRatio >= 0.7) break;
    }
    searchFrom = anchor + 1;
  }
  return bestSpan && bestRatio >= 0.4 ? bestSpan : null;
}

export interface ChainedExactSpan {
  start: number;
  end: number;
}

export interface ChainedExactMatch {
  /** Disjoint exact haystack spans, sorted by position and merged. */
  spans: ChainedExactSpan[];
  /** Fraction of the needle covered by exact fragments (0..1). */
  coverage: number;
}

/**
 * Chained exact fragment matching.
 *
 * Ingested search chunks are not always byte-faithful to pdf.js's own text
 * extraction: Unstructured's fast strategy emits running-header Title and
 * page-number elements BEFORE the page body and splits the running footer
 * across elements, while pdf.js extracts the body first and the footer last.
 * The chunk text (needle) then never occurs contiguously in the page text
 * (haystack), so a whole-needle indexOf fails even though the clause body is
 * fully present.
 *
 * This matcher covers the needle with a chain of its longest exact substrings
 * in the haystack: greedily find the longest common substring, split the
 * needle around it, and repeat on the leftovers. Fragments that genuinely do
 * not occur (headers, split footers) are simply left uncovered; if enough of
 * the needle matches exactly, the spans are returned and callers treat them
 * as exact matches. When the coverage threshold is not met, null is returned
 * and callers fall back to fuzzy matching as before.
 */
interface ChainedFragmentCandidate {
  needleStart: number;
  length: number;
  hayStart: number;
}

function extendSeed(
  segStart: number,
  segEnd: number,
  off: number,
  hayIdx: number,
  haystack: string,
  needle: string,
  minFragment: number,
): ChainedFragmentCandidate {
  let length = minFragment;
  while (
    segStart + off + length < segEnd &&
    hayIdx + length < haystack.length &&
    needle[segStart + off + length] === haystack[hayIdx + length]
  ) {
    length++;
  }
  let left = 0;
  while (
    off - left > 0 &&
    hayIdx - left > 0 &&
    needle[segStart + off - left - 1] === haystack[hayIdx - left - 1]
  ) {
    left++;
  }
  return {
    needleStart: segStart + off - left,
    length: length + left,
    hayStart: hayIdx - left,
  };
}

function mergeSpans(spans: ChainedExactSpan[]): ChainedExactSpan[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const merged: ChainedExactSpan[] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last && span.start <= last.end + 1) {
      last.end = Math.max(last.end, span.end);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

function carveOutSegment(
  segments: Array<[number, number]>,
  best: ChainedFragmentCandidate & { segIdx: number },
  minFragment: number,
): Array<[number, number]> {
  const next: Array<[number, number]> = [];
  for (let i = 0; i < segments.length; i++) {
    if (i !== best.segIdx) {
      next.push(segments[i]);
      continue;
    }
    const [segStart, segEnd] = segments[i];
    const leftEnd = best.needleStart;
    const rightStart = best.needleStart + best.length;
    if (leftEnd - segStart >= minFragment) next.push([segStart, leftEnd]);
    if (segEnd - rightStart >= minFragment) next.push([rightStart, segEnd]);
  }
  return next;
}

function bestFragmentInSegment(
  segment: [number, number],
  haystack: string,
  needle: string,
  minFragment: number,
): ChainedFragmentCandidate | null {
  const [segStart, segEnd] = segment;
  const segLen = segEnd - segStart;
  // Scan seed windows across the segment; extend the first seed present in the
  // haystack in both directions.
  for (let off = 0; off + minFragment <= segLen; off += minFragment) {
    const seed = needle.slice(segStart + off, segStart + off + minFragment);
    const hayIdx = haystack.indexOf(seed);
    if (hayIdx === -1) continue;
    return extendSeed(segStart, segEnd, off, hayIdx, haystack, needle, minFragment);
  }
  return null;
}

function findBestFragment(
  segments: Array<[number, number]>,
  haystack: string,
  needle: string,
  minFragment: number,
): (ChainedFragmentCandidate & { segIdx: number }) | null {
  let best: (ChainedFragmentCandidate & { segIdx: number }) | null = null;
  for (let segIdx = 0; segIdx < segments.length; segIdx++) {
    const candidate = bestFragmentInSegment(segments[segIdx], haystack, needle, minFragment);
    if (candidate && (!best || candidate.length > best.length)) {
      best = { segIdx, ...candidate };
    }
  }
  return best;
}

export function findChainedExactSpans(
  haystack: string,
  needle: string,
  options?: { minFragment?: number; minCoverage?: number },
): ChainedExactMatch | null {
  if (!haystack || !needle) return null;
  const minFragment = Math.max(options?.minFragment ?? 12, 4);
  const minCoverage = options?.minCoverage ?? 0.6;
  if (needle.length < minFragment) return null;

  let segments: Array<[number, number]> = [[0, needle.length]];
  const spans: ChainedExactSpan[] = [];
  let covered = 0;
  // Hard cap so pathological inputs can never loop excessively.
  const maxPasses = 4096;

  for (let pass = 0; pass < maxPasses && segments.length > 0; pass++) {
    const best = findBestFragment(segments, haystack, needle, minFragment);
    if (!best || best.length < minFragment) break;
    covered += best.length;
    spans.push({ start: best.hayStart, end: best.hayStart + best.length });
    segments = carveOutSegment(segments, best, minFragment);
  }

  const coverage = covered / needle.length;
  if (spans.length === 0 || coverage < minCoverage) return null;
  return { spans: mergeSpans(spans), coverage };
}

export function buildTextIndex(textLayerDiv: HTMLElement, boilerplate?: Set<string>) {
  const textDivs = getTextDivs(textLayerDiv);
  let fullText = '';
  const divMap: { div: HTMLElement; start: number; end: number }[] = [];
  for (const div of textDivs) {
    const t = normalise(div.textContent ?? '');
    if (!t) continue;
    if (boilerplate?.has(t.trim())) continue;
    divMap.push({ div, start: fullText.length, end: fullText.length + t.length });
    fullText += t + ' ';
  }
  return { fullText, divMap };
}

/**
 * Header/footer boilerplate detection.
 *
 * Running headers, page titles, and stamps like "Confidential" are extracted
 * by pdf.js into the same per-page text stream as body content. Because they
 * repeat near-identically across many pages (and can land at an arbitrary
 * position relative to the body text pdf.js happens to extract them at),
 * they can coincidentally share words with — or even outscore — the real
 * target chunk during fuzzy matching, causing the highlight to land on a
 * header/footer line instead of the body text the backend actually matched.
 *
 * We detect boilerplate by looking for text-layer divs whose normalised text
 * is repeated near-identically across several pages (>= minPages), using
 * only pages that are already rendered/cached — no extra rendering is
 * triggered purely for this detection, keeping it cheap.
 */
export function collectDivTexts(textLayerDiv: HTMLElement): string[] {
  return getTextDivs(textLayerDiv).map((div) => normalise(div.textContent ?? '').trim());
}

export function detectBoilerplateTexts(pagesDivTexts: string[][], minPages = 3): Set<string> {
  const pageCountByText = new Map<string, number>();
  for (const divTexts of pagesDivTexts) {
    const seenOnThisPage = new Set<string>();
    for (const raw of divTexts) {
      const t = raw.trim();
      // Require a bit of length so we don't flag common short words (e.g.
      // "the", page numbers alone) that legitimately recur everywhere.
      if (t.length < 6) continue;
      if (seenOnThisPage.has(t)) continue;
      seenOnThisPage.add(t);
      pageCountByText.set(t, (pageCountByText.get(t) ?? 0) + 1);
    }
  }
  const boilerplate = new Set<string>();
  for (const [t, count] of pageCountByText) {
    if (count >= minPages) boilerplate.add(t);
  }
  return boilerplate;
}

export function computeBoilerplateFromPageStates(pageStates: Map<number, PageState>): Set<string> {
  const pagesDivTexts: string[][] = [];
  pageStates.forEach((state) => {
    if (state.rendered && state.textLayerDiv) {
      pagesDivTexts.push(collectDivTexts(state.textLayerDiv));
    }
  });
  return detectBoilerplateTexts(pagesDivTexts);
}

export function findInText(fullText: string, needle: string) {
  const idx = fullText.indexOf(needle);
  if (idx !== -1) return { matchIdx: idx, matchEnd: idx + needle.length };
  return fuzzyFindInFullText(fullText, needle);
}

export async function waitForContainerWidth(
  containerRef: React.RefObject<HTMLDivElement>,
): Promise<number> {
  let containerWidth = containerRef.current?.clientWidth ?? 0;
  if (!containerWidth) {
    await new Promise<void>((resolve) => {
      let tries = 0;
      const poll = () => {
        containerWidth = containerRef.current?.clientWidth ?? 0;
        if (containerWidth || ++tries > 15) resolve();
        else requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
    containerWidth = containerRef.current?.clientWidth ?? 600;
  }
  return containerWidth;
}

export const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'with',
  'by',
  'from',
  'is',
  'it',
  'as',
  'be',
  'was',
  'were',
  'been',
  'are',
  'am',
  'do',
  'does',
  'did',
  'has',
  'have',
  'had',
  'not',
  'no',
  'this',
  'that',
  'these',
  'those',
  'what',
  'which',
  'who',
  'whom',
  'how',
  'when',
  'where',
  'why',
  'if',
  'then',
  'so',
  'than',
  'too',
  'very',
  'can',
  'will',
  'just',
  'should',
  'would',
  'could',
  'may',
  'might',
  'shall',
  'about',
  'up',
  'out',
  'into',
]);

export function splitKeywords(phrase: string): string[] {
  return phrase.split(/\s+/).filter((w) => w.length > 1 && !STOP_WORDS.has(w));
}

export type DivMapEntry = { div: HTMLElement; start: number; end: number };
export type WordEntry = { word: string; div: HTMLElement };
export type LineInfo = { top: number; left: number; right: number; fontSize: number };

export type SpanGeometry = { left: number; top: number; fontSize: number };

export function collectAllMatches(
  fullText: string,
  needle: string,
): { matchIdx: number; matchEnd: number }[] {
  if (!needle.trim()) return [];
  const matches: { matchIdx: number; matchEnd: number }[] = [];
  let searchFrom = 0;
  while (true) {
    const idx = fullText.indexOf(needle, searchFrom);
    if (idx === -1) break;
    matches.push({ matchIdx: idx, matchEnd: idx + needle.length });
    searchFrom = idx + 1;
  }
  if (matches.length > 0) return matches;
  for (const word of splitKeywords(needle)) {
    let wordFrom = 0;
    while (true) {
      const idx = fullText.indexOf(word, wordFrom);
      if (idx === -1) break;
      matches.push({ matchIdx: idx, matchEnd: idx + word.length });
      wordFrom = idx + 1;
    }
  }
  if (matches.length > 0) return matches;
  const fuzzy = fuzzyFindInFullText(fullText, splitKeywords(needle).join(' '));
  if (fuzzy) matches.push(fuzzy);
  return matches;
}

export function getMatchingDivs(
  fullText: string,
  divMap: DivMapEntry[],
  needle: string,
  highlightAll: boolean,
): HTMLElement[] {
  let matches: { matchIdx: number; matchEnd: number }[];
  if (highlightAll) {
    matches = collectAllMatches(fullText, needle);
  } else {
    const m = findInText(fullText, needle);
    matches = m ? [m] : [];
  }
  const matchingDivs = new Set<HTMLElement>();
  for (const { matchIdx, matchEnd } of matches) {
    for (const { div, start, end } of divMap) {
      if (end > matchIdx && start < matchEnd) matchingDivs.add(div);
    }
  }
  return Array.from(matchingDivs);
}

export function buildScrollWordList(divMap: DivMapEntry[]): WordEntry[] {
  const wordList: WordEntry[] = [];
  for (const { div } of divMap) {
    const ws = normalise(div.textContent ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    for (const w of ws) wordList.push({ word: w, div });
  }
  return wordList;
}

export function findScrollStart(wordList: WordEntry[], needleWords: string[]): number {
  const checkLen = Math.min(5, needleWords.length);
  for (let i = 0; i <= wordList.length - checkLen; i++) {
    let allMatch = true;
    for (let j = 0; j < checkLen; j++) {
      if (wordList[i + j]?.word !== needleWords[j]) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) return i;
  }
  return -1;
}

export function findFuzzyScrollStart(wordList: WordEntry[], needleWords: string[]): number {
  if (needleWords.length === 0 || wordList.length === 0) return -1;
  const fullText = wordList.map((w) => w.word).join(' ');
  const needle = needleWords.join(' ');
  const fuzzy = fuzzyFindInFullText(fullText, needle);
  if (!fuzzy) return -1;
  // Convert the character offset returned by fuzzyFindInFullText back into a
  // word-list index by counting spaces (word separators) before it.
  let charCount = 0;
  for (let i = 0; i < wordList.length; i++) {
    if (charCount >= fuzzy.matchIdx) return i;
    charCount += wordList[i].word.length + 1;
  }
  return wordList.length - 1;
}

export function walkNeedle(
  wordList: WordEntry[],
  needleWords: string[],
  startIdx: number,
): Set<HTMLElement> {
  const matched = new Set<HTMLElement>();
  if (startIdx < 0) return matched;
  let ni = 0;
  let li = startIdx;
  let skips = 0;
  while (ni < needleWords.length && li < wordList.length && skips < 30) {
    if (wordList[li].word === needleWords[ni]) {
      matched.add(wordList[li].div);
      ni++;
      li++;
      skips = 0;
    } else if (ni + 1 < needleWords.length && wordList[li].word === needleWords[ni + 1]) {
      ni++;
    } else {
      li++;
      skips++;
    }
  }
  return matched;
}

export function parsePx(val: string): number {
  return parseFloat(val) || 0;
}

function getScaleFactor(layer: HTMLElement | null): number {
  const inlineValue = layer?.style.getPropertyValue('--scale-factor');
  let value = inlineValue;
  if (!value && layer && typeof getComputedStyle === 'function') {
    value = getComputedStyle(layer).getPropertyValue('--scale-factor');
  }
  const parsed = parseFloat(value ?? '');
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function getLayerDimension(
  layer: HTMLElement | null,
  axis: 'width' | 'height',
  scaleFactor: number,
): number {
  if (!layer) return 0;
  const dimension = axis === 'width' ? layer.clientWidth : layer.clientHeight;
  if (dimension) return dimension;

  const rect = layer.getBoundingClientRect?.();
  const rectDimension = axis === 'width' ? rect?.width : rect?.height;
  if (rectDimension) return rectDimension;

  const parent = layer.parentElement;
  const parentDimension = parent && (axis === 'width' ? parent.clientWidth : parent.clientHeight);
  if (parentDimension) return parentDimension;

  const styleValue = axis === 'width' ? layer.style.width : layer.style.height;
  const calcMatch = /calc\(\s*var\(--scale-factor\)\s*\*\s*(-?\d+(?:\.\d+)?)px\s*\)/.exec(
    styleValue,
  );
  if (calcMatch) return parseFloat(calcMatch[1]) * scaleFactor;
  return parsePx(styleValue);
}

function parseSpanValue(
  value: string,
  axisSize: number,
  scaleFactor: number,
  parseValue: (value: string) => number,
): number {
  const trimmed = value.trim();
  const percentageMatch = /^(-?\d+(?:\.\d+)?)%$/.exec(trimmed);
  if (percentageMatch) return (parseFloat(percentageMatch[1]) / 100) * axisSize;

  const calcMatch = /calc\(\s*var\(--scale-factor\)\s*\*\s*(-?\d+(?:\.\d+)?)px\s*\)/.exec(trimmed);
  if (calcMatch) return parseFloat(calcMatch[1]) * scaleFactor;
  return parseValue(trimmed);
}

export function resolveSpanGeometry(
  div: HTMLElement,
  layer?: HTMLElement | null,
  parseValue: (value: string) => number = parsePx,
): SpanGeometry {
  const closestLayer = div.closest('.pdf-text-layer');
  const textLayer = layer ?? (closestLayer instanceof HTMLElement ? closestLayer : null);
  const scaleFactor = getScaleFactor(textLayer);
  const width = getLayerDimension(textLayer, 'width', scaleFactor);
  const height = getLayerDimension(textLayer, 'height', scaleFactor);
  const style = div.style;
  const fontSize = parseSpanValue(style.fontSize, 0, scaleFactor, parseValue) || 12;

  return {
    left: parseSpanValue(style.left, width, scaleFactor, parseValue),
    top: parseSpanValue(style.top, height, scaleFactor, parseValue),
    fontSize,
  };
}

export function expandToSameLine(
  baseScrollDivs: HTMLElement[],
  combinedDivMap: DivMapEntry[],
): HTMLElement[] {
  if (baseScrollDivs.length === 0) return [];
  const firstDiv = baseScrollDivs[0];
  const firstGeometry = resolveSpanGeometry(firstDiv);
  const firstWrapper = firstDiv.closest('.pdf-text-layer')?.parentElement;
  const preceding: HTMLElement[] = [];
  for (const { div } of combinedDivMap) {
    if (div === firstDiv) break;
    if (div.closest('.pdf-text-layer')?.parentElement !== firstWrapper) continue;
    const geometry = resolveSpanGeometry(div);
    if (Math.abs(geometry.top - firstGeometry.top) <= firstGeometry.fontSize * 0.6)
      preceding.push(div);
  }
  return [...preceding, ...baseScrollDivs];
}

export function groupDivsByLine(
  divs: HTMLElement[],
  parseValue: (value: string) => number = parsePx,
): Map<number, LineInfo> {
  const lineMap = new Map<number, LineInfo>();
  for (const div of divs) {
    const { top, left, fontSize } = resolveSpanGeometry(div, undefined, parseValue);
    const range = document.createRange();
    range.selectNodeContents(div);
    const measuredWidth =
      range.getBoundingClientRect().width || fontSize * (div.textContent?.length ?? 1) * 0.6;
    const right = left + measuredWidth;
    const lineKey = Math.round(top / (fontSize * 0.6));
    if (!lineMap.has(lineKey)) {
      lineMap.set(lineKey, { top, left, right, fontSize });
    } else {
      const line = lineMap.get(lineKey)!;
      line.left = Math.min(line.left, left);
      line.right = Math.max(line.right, right);
    }
  }
  return lineMap;
}

export interface PageState {
  placeholder: HTMLDivElement;
  rendered: boolean;
  wrapper?: HTMLDivElement;
  textLayerDiv?: HTMLDivElement;
}

export function createOverlay(
  wrapper: HTMLElement,
  div: HTMLElement,
  className: string,
  bg: string,
  viewportWidth: number,
): { el: HTMLElement; top: number } {
  const { left, top, fontSize } = resolveSpanGeometry(div);
  const range = document.createRange();
  range.selectNodeContents(div);
  // pdf.js stretches each span to the PDF line width with scaleX, but the
  // transform has no effect while the span is laid out inline.
  const isInline = getComputedStyle(div).position !== 'absolute';
  const scaleX = isInline
    ? Number.parseFloat(/scaleX\(([\d.]+)\)/.exec(div.style.transform)?.[1] ?? '1') || 1
    : 1;
  const measuredWidth = range.getBoundingClientRect().width * scaleX;
  const textLength = (div.textContent ?? '').replace(/\s+/g, ' ').trim().length;
  const estimatedTextWidth = Math.max(fontSize, textLength * fontSize * 0.65);
  const maxAllowed = Math.max(fontSize, Math.min(viewportWidth - left, estimatedTextWidth));
  const width = Math.max(fontSize, Math.min(measuredWidth, maxAllowed));
  const h = fontSize * 1.2;
  const el = document.createElement('div');
  el.className = className;
  el.style.cssText = `
    position: absolute;
    background: ${bg};
    pointer-events: none;
    left: ${left}px;
    top: ${top}px;
    width: ${width}px;
    height: ${h}px;
  `;
  wrapper.appendChild(el);
  return { el, top };
}

export function applyHighlightsToPage(
  pageNum: number,
  pageStates: Map<number, PageState>,
  highlight: string | undefined,
  highlightAll: boolean,
): { firstTop: number | null } | null {
  const state = pageStates.get(pageNum);
  if (!state?.rendered || !state.textLayerDiv || !state.wrapper) return null;
  const boilerplate = computeBoilerplateFromPageStates(pageStates);
  const { fullText, divMap } = buildTextIndex(state.textLayerDiv, boilerplate);
  const viewportWidth = parseFloat(state.wrapper.style.width) || state.wrapper.offsetWidth || 600;
  let firstTop: number | null = null;
  if (highlight) {
    const needle = normalise(highlight).trim();
    if (needle) {
      const matchingDivs = getMatchingDivs(fullText, divMap, needle, highlightAll);
      for (const div of matchingDivs) {
        const { top } = createOverlay(
          state.wrapper,
          div,
          'pdf-highlight-overlay',
          'rgba(255, 229, 0, 0.25)',
          viewportWidth,
        );
        if (firstTop === null) firstTop = top;
      }
    }
  }
  return { firstTop };
}

export function applyHighlightOverlays(
  highlightAll: boolean,
  targetPage: number,
  pageStates: Map<number, PageState>,
  highlight: string | undefined,
): { firstHighlightTop: number | null; firstHighlightPage: number | null } {
  let firstHighlightTop: number | null = null;
  let firstHighlightPage: number | null = null;
  if (highlightAll) {
    pageStates.forEach((state, pageNum) => {
      if (state.rendered) {
        const result = applyHighlightsToPage(pageNum, pageStates, highlight, highlightAll);
        const top = result?.firstTop ?? null;
        if (top !== null && firstHighlightTop === null) {
          firstHighlightTop = top;
          firstHighlightPage = pageNum;
        }
      }
    });
  } else {
    const result = applyHighlightsToPage(targetPage, pageStates, highlight, highlightAll);
    const top = result?.firstTop ?? null;
    if (top !== null) {
      firstHighlightTop = top;
      firstHighlightPage = targetPage;
    }
  }
  return { firstHighlightTop, firstHighlightPage };
}

export function renderScrollHighlights(
  scrollDivs: HTMLElement[],
  container: HTMLDivElement,
): boolean {
  const wrapperMap = new Map<HTMLElement, HTMLElement[]>();
  for (const div of scrollDivs) {
    const w = div.closest('.pdf-text-layer')?.parentElement as HTMLElement | null;
    if (!w) continue;
    if (!wrapperMap.has(w)) wrapperMap.set(w, []);
    wrapperMap.get(w)!.push(div);
  }
  let firstScrollTop: number | null = null;
  let firstScrollPlaceholder: HTMLElement | null = null;
  for (const [wrapper, divs] of wrapperMap) {
    const lineMap = groupDivsByLine(divs, parsePx);
    for (const line of lineMap.values()) {
      const el = document.createElement('div');
      el.className = 'pdf-scroll-highlight';
      el.style.cssText = `
        position: absolute;
        background: rgba(250, 163, 138, 0.55);
        pointer-events: none;
        left: ${line.left}px;
        top: ${line.top}px;
        width: ${line.right - line.left}px;
        height: ${line.fontSize * 1.4}px;
      `;
      wrapper.appendChild(el);
      if (firstScrollTop === null) {
        firstScrollTop = line.top;
        firstScrollPlaceholder = wrapper.parentElement as HTMLElement;
      }
    }
  }
  if (firstScrollTop === null || !firstScrollPlaceholder) return false;
  const scrollTo = firstScrollPlaceholder.offsetTop + firstScrollTop - container.clientHeight / 2;
  container.scrollTo({ top: Math.max(0, scrollTo), behavior: 'auto' });
  return true;
}

/** Renders every non-target page in the scan window, in order. Returns false if cancelled. */
async function renderScanWindowPages(
  pagesToScan: number[],
  targetPage: number,
  renderPage: (pageNum: number) => Promise<void>,
  cancelledRef: { current: boolean },
): Promise<boolean> {
  for (const pageNum of pagesToScan) {
    if (pageNum === targetPage) continue;
    await renderPage(pageNum);
    if (cancelledRef.current) return false;
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
    if (cancelledRef.current) return false;
  }
  return true;
}

/** Builds a combined div map across the scan window, excluding detected header/footer boilerplate. */
function buildCombinedDivMap(
  pagesToScan: number[],
  pageStates: Map<number, PageState>,
  boilerplate: Set<string>,
): DivMapEntry[] {
  return pagesToScan.flatMap((pageNum) => {
    const pageState = pageStates.get(pageNum);
    if (!pageState?.rendered || !pageState.textLayerDiv) return [];
    return buildTextIndex(pageState.textLayerDiv, boilerplate).divMap;
  });
}

export async function applyScrollTarget(
  targetPage: number,
  scrollTarget: string,
  container: HTMLDivElement,
  pageStatesRef: { current: Map<number, PageState> },
  pdfDocRef: { current: any },
  renderPage: (pageNum: number) => Promise<void>,
  cancelledRef: { current: boolean },
): Promise<boolean> {
  const state = pageStatesRef.current.get(targetPage);
  if (!state?.rendered || !state.textLayerDiv || !state.wrapper) return false;
  const maxPage = pdfDocRef.current?.numPages ?? 0;
  const pagesToScan = [targetPage - 1, targetPage, targetPage + 1].filter(
    (p) => p >= 1 && p <= maxPage,
  );

  const notCancelled = await renderScanWindowPages(
    pagesToScan,
    targetPage,
    renderPage,
    cancelledRef,
  );
  if (!notCancelled) return false;

  // Detect header/footer boilerplate using every page already
  // rendered/cached (not just the target window) — this is free since it
  // reuses text layers already in memory rather than rendering more pages.
  const boilerplate = computeBoilerplateFromPageStates(pageStatesRef.current);
  const combinedDivMap = buildCombinedDivMap(pagesToScan, pageStatesRef.current, boilerplate);

  const scrollNeedle = normalise(scrollTarget);
  const needleWords = scrollNeedle.trim().split(/\s+/).filter(Boolean);
  const wordList = buildScrollWordList(combinedDivMap);
  const exactStartIdx = findScrollStart(wordList, needleWords);
  const startIdx =
    exactStartIdx !== -1 ? exactStartIdx : findFuzzyScrollStart(wordList, needleWords);
  const matchedDivSet =
    startIdx !== -1 ? walkNeedle(wordList, needleWords, startIdx) : new Set<HTMLElement>();
  const scrollDivs = expandToSameLine(Array.from(matchedDivSet), combinedDivMap);
  if (scrollDivs.length === 0) return false;
  return renderScrollHighlights(scrollDivs, container);
}

export function scrollToHighlight(
  scrollTarget: string | undefined,
  firstHighlightTop: number | null,
  firstHighlightPage: number | null,
  targetPage: number,
  container: HTMLDivElement,
  pageStates: Map<number, PageState>,
): void {
  if (scrollTarget) {
    container
      .querySelector(`[data-page="${targetPage}"]`)
      ?.scrollIntoView({ behavior: 'auto', block: 'start' });
  } else if (firstHighlightTop !== null && firstHighlightPage !== null) {
    const state = pageStates.get(firstHighlightPage);
    if (state) {
      const scrollTo = state.placeholder.offsetTop + firstHighlightTop - container.clientHeight / 2;
      container.scrollTo({ top: Math.max(0, scrollTo), behavior: 'smooth' });
    }
  }
}
